<template>
  <div class="py-1 fill-height">
    <v-card color="project-card grey lighten-5 fill-height" elevation="2" @click="goToProject">
      <v-row no-gutters>
        <!-- For unscheduled on mobile: just the menu button (no drag needed) -->
        <v-menu v-if="showAssignButtonOnly" v-model="assignSheetOpen" offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-col
              v-bind="attrs"
              v-on="on"
              class="primary pa-0 d-flex align-center justify-center"
              style="max-width: 24px; width: 24px; max-height: 100%; cursor: pointer"
              @click.stop
            >
              <v-icon color="white" small>mdi-plus</v-icon>
            </v-col>
          </template>
          <v-list dense>
            <v-subheader>{{ $t('production_timeline.assign_to_line') }}</v-subheader>
            <v-list-item v-for="line in productionLines" :key="line.id" @click="selectLine(line)">
              <v-list-item-icon>
                <v-icon>mdi-factory</v-icon>
              </v-list-item-icon>
              <v-list-item-title>{{ line.name }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- For scheduled on mobile: drag handle + menu button side by side -->
        <template v-else-if="showBothControls">
          <v-col
            :class="[showHandle ? 'handle' : '']"
            class="grey lighten-3 pa-0 d-flex align-center justify-center"
            style="max-width: 24px; width: 24px; max-height: 100%"
          >
            <v-icon v-if="showHandle">mdi mdi-drag-vertical</v-icon>
          </v-col>
          <v-menu v-model="moveMenuOpen" offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-col
                v-bind="attrs"
                v-on="on"
                class="grey lighten-2 pa-0 d-flex align-center justify-center"
                style="max-width: 24px; width: 24px; max-height: 100%; cursor: pointer"
                @click.stop
              >
                <v-icon small>mdi-swap-horizontal</v-icon>
              </v-col>
            </template>
            <v-list dense>
              <v-subheader>{{ $t('production_timeline.move_to_line') }}</v-subheader>
              <v-list-item
                v-for="line in productionLines"
                :key="line.id"
                :disabled="line.id === projectLog.productionLineId"
                @click="selectLine(line)"
              >
                <v-list-item-icon>
                  <v-icon>{{ line.id === projectLog.productionLineId ? 'mdi-check' : 'mdi-factory' }}</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ line.name }}</v-list-item-title>
              </v-list-item>
              <v-divider />
              <v-list-item @click="unschedule">
                <v-list-item-icon>
                  <v-icon>mdi-calendar-remove</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('production_timeline.remove_from_schedule') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <!-- Desktop or non-admin: just drag handle (original behavior) -->
        <v-col
          v-else
          :class="[showHandle ? 'handle' : '']"
          class="grey lighten-3 pa-0 d-flex align-center justify-center"
          style="max-width: 24px; width: 24px; max-height: 100%"
        >
          <v-icon v-if="showHandle">mdi mdi-drag-vertical</v-icon>
        </v-col>
        <v-col>
          <v-card-title
            class="sans--text caption text-md-body-2 text-lg-body-1 font-weight-medium px-2 pt-2 pb-0"
            style="line-height: 1.1rem"
          >
            <div style="word-break: break-word; width: 100%">
              <v-row no-gutters>
                <status-chip :status="projectLog.status" />
                <v-spacer></v-spacer>
                <div v-if="hasStartAndEndDate" class="caption text-right">
                  <div>
                    <template v-if="$vuetify.breakpoint.smAndUp && doneBeforeDeliveryDuration >= 0">
                      {{ $t('done') }}
                      <v-avatar :color="doneBeforeDeliveryDurationColor" class="ma-0 mt-n1 white--text" size="27">
                        {{ doneBeforeDeliveryDuration }}
                      </v-avatar>
                      {{ $t('production_timeline.before_delivery') }}
                    </template>
                    <template v-else-if="$vuetify.breakpoint.smAndUp && doneBeforeDeliveryDuration < 0">
                      {{ $t('production_timeline.will_be_late') }}
                      <v-avatar :color="doneBeforeDeliveryDurationColor" class="ma-0 mt-n1 white--text" size="27">
                        {{ doneBeforeDeliveryDuration * -1 }}
                      </v-avatar>
                      {{ $t('production_timeline.from_delivery') }}
                    </template>
                    <template v-else>
                      <v-avatar :color="doneBeforeDeliveryDurationColor" class="ma-0 mt-n1 white--text" size="27">
                        {{ doneBeforeDeliveryDuration }}
                      </v-avatar>
                    </template>
                  </div>
                </div>
              </v-row>
              <div class="mt-2">
                <span class="grey--text">{{ projectLog.project.code }}</span>
                {{ projectLog.project.name }}
              </div>
            </div>
          </v-card-title>
          <v-card-text v-if="$vuetify.breakpoint.mdAndUp || cardExpanded" class="py-0 px-2 caption mt-2">
            <div :key="projectLog.id">
              <div v-if="projectLog.log" :key="`${projectLog.id}`">
                <v-row no-gutters>
                  <v-col>
                    <strong>{{ projectLog.amount }} {{ projectLog.log.unit }}</strong>
                    <span v-if="projectLog.doneAmountPercent" class="font-italic"
                      >({{ $formatNumberInt(projectLog.doneAmountPercent) }} %)</span
                    >
                    {{ projectLog.log.description }}
                    {{ projectLog.log.description2 }}
                    <template v-if="projectLog.logSupplier"
                      >, <strong class="grey--text text--darken-1">{{ projectLog.logSupplier }}</strong></template
                    >
                  </v-col>
                </v-row>
              </div>
            </div>
            <div v-if="projectLog.description" class="caption py-2">
              <v-icon :size="$vuetify.breakpoint.smAndDown ? '18' : '18'" class="mt-n1 mr-1">mdi mdi-information-outline </v-icon>
              {{ projectLog.description }}
            </div>

            <div v-if="projectLog.project.notice" class="font-italic mt-3 mt-md-2">
              <v-icon :size="$vuetify.breakpoint.smAndDown ? '18' : '18'" class="mt-n1 mr-1">mdi mdi-information-outline </v-icon>
              {{ projectLog.project.notice }}
            </div>
          </v-card-text>

          <v-card-text v-if="$vuetify.breakpoint.mdAndUp || cardExpanded" class="px-2 py-0 my-1 mt-2">
            <production-details-chip
              :check="projectLog.project.basicMeasurementGuide"
              :description="projectLog.project.basicMeasurementGuideDescription"
              :small="$vuetify.breakpoint.lgAndUp"
              :x-small="$vuetify.breakpoint.mdAndDown"
              title="PMO"
            />
            <production-details-chip
              :check="projectLog.project.workPictures"
              :description="projectLog.project.workPicturesDescription"
              :small="$vuetify.breakpoint.lgAndUp"
              :x-small="$vuetify.breakpoint.mdAndDown"
              title="TYÖ"
            />
            <production-details-chip
              :check="projectLog.project.constructionDrawings"
              :description="projectLog.project.constructionDrawingsDescription"
              :small="$vuetify.breakpoint.lgAndUp"
              :x-small="$vuetify.breakpoint.mdAndDown"
              title="RAK"
            />
            <production-details-chip
              :check="projectLog.project.bvn"
              :description="projectLog.project.bvnDescription"
              :small="$vuetify.breakpoint.lgAndUp"
              :x-small="$vuetify.breakpoint.mdAndDown"
              title="BVN"
            />
            <production-details-chip
              :check="projectLog.project.buildingPermission"
              :description="projectLog.project.buildingPermissionDescription"
              :small="$vuetify.breakpoint.lgAndUp"
              :x-small="$vuetify.breakpoint.mdAndDown"
              title="LUPA"
            />
            <production-details-chip
              :check="projectLog.project.materialsList"
              :description="projectLog.project.materialsListDescription"
              :small="$vuetify.breakpoint.smAndUp"
              :x-small="$vuetify.breakpoint.xsOnly"
              title="TAVL"
            />
            <production-details-chip
              :check="projectLog.project.windowsAndDoors"
              :description="projectLog.project.windowsAndDoorsDescription"
              :small="$vuetify.breakpoint.smAndUp"
              :x-small="$vuetify.breakpoint.xsOnly"
              title="I&O"
            />
          </v-card-text>

          <v-card-text class="px-2 py-2 caption">
            <div>
              <v-row no-gutters style="position: relative;">
                <div style="position: absolute; bottom: 0; right: 0;">
                  <v-btn v-if="$vuetify.breakpoint.smAndDown" icon class="ml-1" @click.stop="cardExpanded = !cardExpanded">
                    <v-icon>{{ cardExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                  </v-btn>
                </div>
                <v-col cols="12" sm="8">
                  <template v-if="hasStartAndEndDate && ($vuetify.breakpoint.mdAndUp || cardExpanded)"
                    >{{ $t('production_timeline.production') }}:
                    <span class="text-uppercase">{{ $formatWeekDay(projectLog.productionStartDate) }} </span>
                    <span>{{ $formatDayMonth(projectLog.productionStartDate) }}</span>
                    -
                    <span class="text-uppercase">{{ $formatWeekDay(projectLog.productionEndDate) }} </span>
                    <span>{{ $formatDayMonth(projectLog.productionEndDate) }}</span>
                  </template>
                  <template v-if="deliveryDate">
                    <span :class="[hasStartAndEndDate && ($vuetify.breakpoint.mdAndUp || cardExpanded) ? 'ml-2' : '']">
                      {{ $t('production_timeline.delivery_date') }}: {{ $formatDateNoTime(deliveryDate) }}</span
                    >
                  </template>
                  <template v-if="projectLog.project.drawerName && ($vuetify.breakpoint.mdAndUp || cardExpanded)">
                    <span :class="[deliveryDate ? 'ml-2' : '']">
                      {{ $t('project.drawer_name_short') }}: {{ projectLog.project.drawerName }}</span
                    >
                  </template>
                  <template v-if="!projectLog.project.drawerName && ($vuetify.breakpoint.mdAndUp || cardExpanded)">
                    <span :class="[deliveryDate ? 'ml-2' : '', 'error--text']">
                      <v-icon color="error" size="18">mdi mdi-alert</v-icon> {{ $t('project.drawer_missing') }}!</span
                    >
                  </template>
                </v-col>
                <v-col class="text-left text-sm-right" cols="12" sm="4">
                  {{ $t('production_timeline.production_time') }}:
                  {{ projectLog.productionTime ? `${projectLog.productionTime}h` : '-' }}
                </v-col>
              </v-row>
            </div>
          </v-card-text>
        </v-col>
      </v-row>
    </v-card>
  </div>
</template>

<script>
import StatusChip from '@/views/tenant/ProductionTimelineView/components/StatusChip';
import ProductionDetailsChip from '@/views/tenant/ProductionTimelineView/components/ProductionDetailsChip';
import dayjs from 'dayjs';

export default {
  name: 'ProjectCard',
  components: { ProductionDetailsChip, StatusChip },
  props: {
    projectLog: {
      type: Object,
      required: true,
    },
    updating: {
      type: Boolean,
      default: false,
    },
    productionLines: {
      type: Array,
      default: () => [],
    },
    isUnscheduled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      cardExpanded: false,
      assignSheetOpen: false,
      moveMenuOpen: false,
    };
  },
  methods: {
    goToProject() {
      this.$router.push({ name: 'tenant_project', params: { id: this.projectLog.project.id } });
    },
    selectLine(line) {
      this.assignSheetOpen = false;
      this.moveMenuOpen = false;
      this.$emit('assign-to-line', { projectLog: this.projectLog, productionLine: line });
    },
    unschedule() {
      this.moveMenuOpen = false;
      this.$emit('unschedule', { projectLog: this.projectLog });
    },
  },
  computed: {
    // Show only assign button (unscheduled projects on mobile)
    showAssignButtonOnly() {
      return this.isUnscheduled && this.$isTenantAdmin && this.$vuetify.breakpoint.smAndDown && this.productionLines.length > 0;
    },
    // Show both drag handle + menu button (scheduled projects on mobile with multiple lines)
    showBothControls() {
      return !this.isUnscheduled && this.$isTenantAdmin && this.$vuetify.breakpoint.smAndDown && this.productionLines.length > 1;
    },
    showHandle() {
      return ['CANT_START', 'CAN_START', 'PAUSED'].includes(this.projectLog.status) && this.$isTenantAdmin;
    },
    hasStartAndEndDate() {
      return this.projectLog.productionStartDate && this.projectLog.productionEndDate;
    },
    deliveryDate() {
      // Support #138 Toimituspäivän muutos aikatauluun
      return this.projectLog.project.estimatedEnd || this.projectLog.project.deliveryDate;
    },
    doneBeforeDeliveryDuration() {
      if (this.projectLog?.productionEndDate && this.deliveryDate) {
        return dayjs(this.deliveryDate).diff(dayjs(this.projectLog.productionEndDate), 'days');
      }
      return 0;
    },
    doneBeforeDeliveryDurationColor() {
      if (this.doneBeforeDeliveryDuration > 6) return 'grey';
      if (this.doneBeforeDeliveryDuration > 0) return 'orange';
      return 'red';
    },
  },
};
</script>

<style lang="scss" scoped>
.project-card {
  .handle {
    cursor: move;
  }
}
</style>
